/**
 * Urgent Inspection — the movement area walked after bad weather, before
 * operations are confirmed safe. The rules are in
 * `domain/urgentInspection.ts`; this is the screen.
 *
 * One inspection is open at a time. The runways are shown as three thirds
 * each, counted from the lower-designator threshold as the runway condition
 * report is; the taxiways and aprons as one tile each. Picking an area opens
 * its check beneath; saving it appends to the record, so a re-inspection never
 * hides the first one. Operations are confirmed safe only when every area is
 * inspected and nothing is open; otherwise the inspection closes with the
 * restricted areas named.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { putNote, readLocal } from "../shared/noteStore";
import { useSession } from "../../app/SessionContext";
import {
  answerUrgent,
  areasOf,
  buildUrgentDesk,
  CHECK_LABEL,
  CHECKS,
  CHECKS_FOR,
  checkProblems,
  closeInspection,
  CONTAMINANT,
  CONTAMINANTS,
  coverageBand,
  materialForUrgent,
  mergeUrgent,
  noteWorkOrder,
  openInspection,
  recordCheck,
  restrictedAreas,
  RWYCC_LABEL,
  rwyccFor,
  STATE_LABEL,
  statesOf,
  THIRD_LABEL,
  TRIGGERS,
  URGENT_MODEL_PROMPT,
  VERDICT_LABEL,
  type Area,
  type AreaCheck,
  type AreaState,
  type CheckKey,
  type CheckState,
  type Contaminant,
  type TriggerKey,
  type UrgentInspection,
  type UrgentRegister,
  type Verdict,
  type Who,
} from "../../domain/urgentInspection";
import { mockUrgent, OEMA_MOVEMENT_AREA } from "../../mock/urgentInspection";
import { Button, Card, EmptyState, Field, Input, Select } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { AskCard, Tiles, x as ex } from "../executive/shared";
import { useScreenContext } from "../../assistant/screen";
import { localStamp } from "../equipment/FleetEditors";
import { t, tn } from "../../i18n";
import { UrgentForms } from "./UrgentForms";
import { FORMS, RECIPIENTS } from "../../domain/urgentForms";
import s from "./Urgent.module.css";

export const URGENT_TOPIC = "aerodrome.urgent-inspections";

const TONE: Record<Verdict, string> = {
  "not-inspected": s.vNone,
  clear: s.vClear,
  caution: s.vCaution,
  restricted: s.vBad,
  unsafe: s.vBad,
};

const tagFor = (v: Verdict) =>
  v === "clear"
    ? ex.tagGreen
    : v === "unsafe" || v === "restricted"
      ? ex.tagRed
      : v === "caution"
        ? ex.tag
        : ex.tagBlue;

/** "RWY 17/35 — First third", "Taxiway B" — translated. */
export function areaName(a: Area): string {
  return a.third ? `${a.name} — ${t(THIRD_LABEL[a.third])}` : t(a.name);
}

/**
 * The record as every device wrote it.
 *
 * Each phone and this console post their own copy under one topic and never
 * edit one, so the record is the merge of all of them (see `mergeUrgent`) —
 * not the latest, which would let a phone's copy hide what the console did
 * and the other way round. Read again every half minute, so an area a phone
 * checks appears here without anybody reloading.
 */
function useUrgentRecord() {
  const session = useSession();
  const qc = useQueryClient();
  const siteId = session.site.id;
  /* build 7: the sample "heavy rain" event is the demo's alone; the live platform starts with no inspection */
  const platform = session.source.kind === "platform";
  const seed = useMemo<UrgentRegister>(() => (platform ? { inspections: [] } : mockUrgent()), [platform]);
  const q = useQuery({
    queryKey: ["urgent-record", siteId],
    staleTime: 15_000,
    refetchInterval: 30_000,
    queryFn: async (): Promise<UrgentRegister | null> => {
      const copies: UrgentRegister[] = [];
      try {
        for (const n of await session.source.listNotes(siteId, URGENT_TOPIC))
          try {
            const v = JSON.parse(n.body) as UrgentRegister;
            if (Array.isArray(v?.inspections)) copies.push(v);
          } catch {
            /* not a record */
          }
      } catch {
        /* no platform; the copy in this browser stands */
      }
      const kept = readLocal<UrgentRegister>(siteId, URGENT_TOPIC);
      if (kept?.inspections) copies.push(kept);
      return copies.length ? mergeUrgent(copies) : null;
    },
  });
  const [mine, setMine] = useState<UrgentRegister | null>(null);
  const [savedTo, setSavedTo] = useState<string | null>(null);
  const value = useMemo(
    () => mergeUrgent([q.data ?? (mine ? { inspections: [] } : seed), ...(mine ? [mine] : [])]),
    [q.data, mine, seed],
  );
  return {
    value,
    savedTo,
    save: async (next: UrgentRegister) => {
      setMine(next);
      const where = await putNote(session.source, siteId, URGENT_TOPIC, next);
      setSavedTo(
        where === "platform"
          ? t("saved on the platform under {who}", { who: session.principal.name })
          : t("saved in this browser only — the platform did not take the note"),
      );
      void qc.invalidateQueries({ queryKey: ["urgent-record", siteId] });
    },
  };
}

export function UrgentInspectionPage() {
  const session = useSession();
  const entered = useUrgentRecord();
  const reg = entered.value;
  const desk = useMemo(() => buildUrgentDesk(reg, OEMA_MOVEMENT_AREA, new Date()), [reg]);
  const [picked, setPicked] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const who = (): Who => ({
    by: session.principal.name,
    at: new Date().toISOString(),
    newId: () => `ui-${crypto.randomUUID().slice(0, 8)}`,
  });
  const save = (next: UrgentRegister) => entered.save(next);
  const pickedState = desk.states.find((x) => x.area.id === picked) ?? null;

  useScreenContext({
    route: "/urgent-inspection",
    title: "Urgent Inspection",
    summary: desk.boardLine,
    material: materialForUrgent(desk),
    figures: desk.figures,
    own: (q) => answerUrgent(q, desk),
    prompt: URGENT_MODEL_PROMPT,
    offers: desk.open
      ? [
          { label: "What is left?", ask: "which areas are still to inspect?" },
          { label: "What is not safe?", ask: "which areas have a hazard open?" },
          { label: "Runway condition", ask: "what is the runway condition code?" },
        ]
      : [{ label: "Last inspection", ask: "what was the last urgent inspection status?" }],
  });

  return (
    <Page
      eyebrow={t("{site} · Aerodrome", { site: session.site.code })}
      title="Urgent Inspection"
      actions={
        <div className={s.actions}>
          {!desk.open && (
            <Button variant="primary" onClick={() => setStarting(true)} data-testid="urgent-start">
              {t("Start an urgent inspection")}
            </Button>
          )}
          <button type="button" className={ex.tag} onClick={() => window.print()}>
            {t("Print")}
          </button>
        </div>
      }
    >
      <p className={ex.lead}>
        {t(
          "After severe weather, inspect every runway third, taxiway, and apron before confirming operations are safe.",
        )}
      </p>
      <p className={s.basis} data-testid="urgent-basis">
        {t(
          "Based on ICAO Annex 14 Vol. I and PANS-Aerodromes (Doc 9981). Codes here are a working aid, not the issued report.",
        )}
      </p>

      <Tiles items={desk.tiles} currency="SAR" />
      {entered.savedTo && <p className={ex.saved}>{entered.savedTo}</p>}

      {starting && (
        <StartForm
          onCancel={() => setStarting(false)}
          onStart={async (trigger, note) => {
            await save(openInspection(reg, trigger, note, who()));
            setStarting(false);
          }}
        />
      )}

      {desk.alsoOpen.length > 0 && (
        <div className={s.banner + " " + s.bannerWait} data-testid="urgent-also-open">
          {t(
            "Another urgent inspection is open, started {when} by {who}. This screen uses the earlier one. Close the other when done.",
            { when: localStamp(desk.alsoOpen[0].openedAt), who: desk.alsoOpen[0].openedBy },
          )}
        </div>
      )}

      {desk.open && (
        <>
          <OpenHeader
            insp={desk.open}
            states={desk.states}
            progress={desk.progress!}
            onClose={async (outcome, note) => {
              await save(closeInspection(reg, desk.open!.id, desk.areas, outcome, note, who()));
              setPicked(null);
            }}
          />

          <Card
            title="Runways"
            sub="In thirds, from the lower-numbered threshold"
          >
            <div className={s.runways}>
              {desk.rcr.map(({ runway, line }) => (
                <div key={runway.zone} className={s.runway} data-testid={`runway-${runway.zone}`}>
                  <div className={s.runwayHead}>
                    <b>RWY {runway.designator}</b>
                    {runway.lengthM && <span className={ex.muted}>{runway.lengthM.toLocaleString("en")} m</span>}
                    <span className={ex.muted}>{t("from threshold {n}", { n: runway.from })}</span>
                  </div>
                  <div className={s.thirds}>
                    {desk.states
                      .filter((x) => x.area.zone === runway.zone)
                      .map((x) => (
                        <ThirdTile
                          key={x.area.id}
                          st={x}
                          active={picked === x.area.id}
                          onPick={() => setPicked(x.area.id)}
                        />
                      ))}
                  </div>
                  <div className={s.rcr} data-testid={`rcr-${runway.zone}`}>
                    <span className={ex.muted}>{t("Runway condition report, draft:")}</span>{" "}
                    {line ? <code>{line}</code> : <i>{t("incomplete — not every third has been assessed")}</i>}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card
            title="Taxiways"
            sub="Water, ice, sand, FOD, damage, lights, markings"
          >
            <div className={s.tiles}>
              {desk.states
                .filter((x) => x.area.kind === "taxiway")
                .map((x) => (
                  <AreaTile key={x.area.id} st={x} active={picked === x.area.id} onPick={() => setPicked(x.area.id)} />
                ))}
            </div>
          </Card>

          <Card
            title="Aprons"
            sub="Debris, moved equipment, blocked stands, flooding, lights"
          >
            <div className={s.tiles}>
              {desk.states
                .filter((x) => x.area.kind === "apron")
                .map((x) => (
                  <AreaTile key={x.area.id} st={x} active={picked === x.area.id} onPick={() => setPicked(x.area.id)} />
                ))}
            </div>
          </Card>

          {pickedState && (
            <AreaEditor
              key={`${pickedState.area.id}-${pickedState.times}`}
              st={pickedState}
              onCancel={() => setPicked(null)}
              onSave={async (c) => {
                await save(recordCheck(reg, desk.open!.id, pickedState.area, c, who()));
              }}
              onWorkOrder={async (number) => {
                await save(noteWorkOrder(reg, desk.open!.id, pickedState.area.id, number, who()));
              }}
              history={desk.open.checks.filter((c) => c.areaId === pickedState.area.id)}
            />
          )}

          <Card title="Log of this inspection" sub="Every action, by whom, and when">
            <Log insp={desk.open} />
          </Card>
        </>
      )}

      {!desk.open && !starting && (
        <Card title="No urgent inspection is open">
          <p className={ex.muted}>
            {t(
              "Start one after any weather that may have changed the airfield.",
            )}
          </p>
        </Card>
      )}

      <UrgentForms reg={reg} onSave={save} />

      <AskCard
        title="The line for the duty manager"
        sub="Written from the inspection record"
        board={desk.boardLine}
        material={materialForUrgent(desk)}
        figures={desk.figures}
        own={(q) => answerUrgent(q, desk)}
        prompt={URGENT_MODEL_PROMPT}
        placeholder={t("What is left to inspect? What is not safe? What is the runway condition code?")}
      />

      <Card title="Past urgent inspections" sub="Cause, duration, and outcome">
        <History list={desk.history} />
      </Card>

      <Checklist />
    </Page>
  );
}

/* ── starting ─────────────────────────────────────────────────────────── */

function StartForm({
  onStart,
  onCancel,
}: {
  onStart: (t: TriggerKey, note: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [trigger, setTrigger] = useState<TriggerKey>("rain");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const watch = TRIGGERS.find((x) => x.key === trigger)!.watch;
  return (
    <Card
      title="Start an urgent inspection"
      sub="Pick the weather, then inspect every area"
    >
      <div className={ex.row}>
        <Field label="Weather event">
          <Select
            value={trigger}
            onChange={(e) => setTrigger(e.target.value as TriggerKey)}
            aria-label={t("Weather event")}
          >
            {TRIGGERS.map((x) => (
              <option key={x.key} value={x.key}>
                {t(x.label)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="What happened (optional)">
          <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="urgent-note" />
        </Field>
      </div>
      <p className={ex.muted}>{t("Look especially for: {what}.", { what: t(watch) })}</p>
      <div className={s.actions}>
        <Button onClick={onCancel} disabled={busy}>
          {t("Cancel")}
        </Button>
        <Button
          variant="primary"
          disabled={busy}
          data-testid="urgent-open"
          onClick={async () => {
            setBusy(true);
            try {
              await onStart(trigger, note);
            } finally {
              setBusy(false);
            }
          }}
        >
          {t("Open the inspection")}
        </Button>
      </div>
    </Card>
  );
}

/* ── the open inspection ──────────────────────────────────────────────── */

function OpenHeader({
  insp,
  states,
  progress,
  onClose,
}: {
  insp: UrgentInspection;
  states: AreaState[];
  progress: "in-progress" | "ready" | "restricted";
  onClose: (outcome: "safe" | "restricted", note?: string) => Promise<void>;
}) {
  const [note, setNote] = useState("");
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const trig = TRIGGERS.find((x) => x.key === insp.trigger)!;
  const bad = restrictedAreas(states);
  const left = states.filter((x) => x.verdict === "not-inspected").length;
  const run = async (outcome: "safe" | "restricted") => {
    setFailed(null);
    if (outcome === "restricted" && !note.trim()) return setFailed(t("Say what stays restricted and why."));
    setBusy(true);
    try {
      await onClose(outcome, note);
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card
      title="Inspection in progress"
      sub={t("after {trigger} · opened {when} by {who}", {
        trigger: t(trig.label).toLowerCase(),
        when: localStamp(insp.openedAt),
        who: insp.openedBy,
      })}
    >
      {insp.triggerNote && <p className={ex.muted}>{insp.triggerNote}</p>}
      <div
        className={`${s.banner} ${progress === "ready" ? s.bannerOk : progress === "restricted" ? s.bannerBad : s.bannerWait}`}
        data-testid="urgent-progress"
      >
        {progress === "in-progress" &&
          tn(
            left,
            "Operations are not confirmed: {n} area is still to inspect.",
            "Operations are not confirmed: {n} areas are still to inspect.",
          )}
        {progress === "ready" && t("Every area is inspected and no hazard is open. Operations can be confirmed safe.")}
        {progress === "restricted" &&
          t("Every area is inspected, but these are not safe: {areas}.", {
            areas: bad.map((x) => areaName(x.area)).join(", "),
          })}
      </div>
      {progress !== "in-progress" && (
        <div className={s.closeRow}>
          {progress === "ready" && (
            <Button variant="primary" onClick={() => void run("safe")} disabled={busy} data-testid="urgent-confirm">
              {t("Confirm safe operations")}
            </Button>
          )}
          <Field label={progress === "ready" ? "Note (optional)" : "What stays restricted, and why"}>
            <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="urgent-close-note" />
          </Field>
          {progress === "restricted" && (
            <Button
              variant="primary"
              onClick={() => void run("restricted")}
              disabled={busy}
              data-testid="urgent-restrict"
            >
              {t("Close with restrictions")}
            </Button>
          )}
        </div>
      )}
      {failed && (
        <p className={s.err} role="alert">
          {failed}
        </p>
      )}
    </Card>
  );
}

function ThirdTile({ st, active, onPick }: { st: AreaState; active: boolean; onPick: () => void }) {
  const c = st.latest;
  return (
    <button
      type="button"
      className={`${s.third} ${TONE[st.verdict]} ${active ? s.active : ""}`}
      onClick={onPick}
      aria-pressed={active}
      data-testid={`area-${st.area.id}`}
    >
      <span className={s.thirdLabel}>{t(THIRD_LABEL[st.area.third!])}</span>
      <span className={s.code}>{st.rwycc ?? "—"}</span>
      <span className={s.small}>
        {c?.contaminant ? t(CONTAMINANT[c.contaminant].label) : t("not inspected")}
        {c?.contaminant && c.contaminant !== "dry" && c.coveragePct != null && ` · ${c.coveragePct}%`}
        {c?.depthMm != null && CONTAMINANT[c.contaminant!]?.depth && ` · ${c.depthMm} mm`}
      </span>
      <Findings st={st} />
    </button>
  );
}

function AreaTile({ st, active, onPick }: { st: AreaState; active: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      className={`${s.area} ${TONE[st.verdict]} ${active ? s.active : ""}`}
      onClick={onPick}
      aria-pressed={active}
      data-testid={`area-${st.area.id}`}
    >
      <b>{t(st.area.name)}</b>
      <span className={tagFor(st.verdict)}>{t(VERDICT_LABEL[st.verdict])}</span>
      <Findings st={st} />
    </button>
  );
}

function Findings({ st }: { st: AreaState }) {
  if (!st.hazards.length && !st.cleared.length) return null;
  return (
    <span className={s.findings}>
      {st.hazards.map((k) => (
        <span key={k} className={s.hazard}>
          {t(CHECK_LABEL[k])}
        </span>
      ))}
      {st.cleared.map((k) => (
        <span key={k} className={s.cleared}>
          {t("{what} — cleared", { what: t(CHECK_LABEL[k]) })}
        </span>
      ))}
    </span>
  );
}

/* ── checking one area ────────────────────────────────────────────────── */

type Draft = Omit<AreaCheck, "id" | "at" | "by" | "areaId">;

function AreaEditor({
  st,
  history,
  onSave,
  onCancel,
  onWorkOrder,
}: {
  st: AreaState;
  history: AreaCheck[];
  onSave: (c: Draft) => Promise<void>;
  onCancel: () => void;
  onWorkOrder: (number: string) => Promise<void>;
}) {
  const session = useSession();
  const area = st.area;
  const prev = st.latest;
  const [d, setD] = useState<Draft>(() =>
    prev
      ? {
          checks: { ...prev.checks },
          notes: { ...prev.notes },
          contaminant: prev.contaminant,
          depthMm: prev.depthMm,
          coveragePct: prev.coveragePct,
          oatAtOrBelowMinus15: prev.oatAtOrBelowMinus15,
        }
      : { checks: {}, notes: {} },
  );
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [raised, setRaised] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const problems = checkProblems(area, d);
  const problem = (f: string) => (tried ? problems.find((p) => p.field === f) : undefined);
  const matrix =
    area.kind === "runway" && d.contaminant
      ? rwyccFor(d.contaminant, d.depthMm, d.coveragePct ?? 100, d.oatAtOrBelowMinus15)
      : null;
  const band = d.coveragePct != null ? coverageBand(d.coveragePct) : null;
  const found = CHECKS_FOR[area.kind].filter((k) => d.checks[k] === "found");

  const set = (k: CheckKey, v: CheckState) => setD((x) => ({ ...x, checks: { ...x.checks, [k]: v } }));
  const num = (v: string) => (v.trim() === "" ? undefined : Number(v));

  const submit = async () => {
    setTried(true);
    if (problems.length) return;
    setBusy(true);
    setFailed(null);
    try {
      await onSave(d);
      setSaved(true);
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const raise = async () => {
    setFailed(null);
    try {
      const wo = await session.source.createWorkOrder({
        siteId: session.site.id,
        title: `${areaName(area)} — ${found.map((k) => t(CHECK_LABEL[k])).join(", ")}`,
        kind: "corrective",
        priority: 1,
        zoneCode: area.zone,
        description: [
          t("Found in the urgent inspection after bad weather."),
          ...found.map((k) => `• ${t(CHECK_LABEL[k])}${d.notes?.[k] ? `: ${d.notes[k]}` : ""}`),
        ].join("\n"),
      });
      const number = wo.number ?? wo.id;
      setRaised(number);
      await onWorkOrder(number);
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Card
      title={t("Inspect {area}", { area: areaName(area) })}
      sub={
        prev
          ? t("checked {n} times; the last by {who} at {when}. Saving adds a new check and keeps the earlier ones.", {
              n: st.times,
              who: prev.by,
              when: localStamp(prev.at),
            })
          : t("not inspected yet in this inspection")
      }
    >
      <div data-testid="urgent-editor">
        {area.kind === "runway" && (
          <div className={s.surface}>
            <Field label="Surface condition">
              <Select
                value={d.contaminant ?? ""}
                onChange={(e) => setD((x) => ({ ...x, contaminant: (e.target.value || undefined) as Contaminant }))}
                aria-label={t("Surface condition")}
                data-testid="urgent-contaminant"
              >
                <option value="">{t("Choose…")}</option>
                {CONTAMINANTS.map((c) => (
                  <option key={c.key} value={c.key}>
                    {t(c.label)}
                  </option>
                ))}
              </Select>
              {problem("contaminant") && <span className={s.err}>{t(problem("contaminant")!.message)}</span>}
            </Field>
            {d.contaminant && d.contaminant !== "dry" && (
              <Field label="Coverage of this third, %">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={d.coveragePct ?? ""}
                  onChange={(e) => setD((x) => ({ ...x, coveragePct: num(e.target.value) }))}
                  data-testid="urgent-coverage"
                />
                {problem("coveragePct") && <span className={s.err}>{t(problem("coveragePct")!.message)}</span>}
              </Field>
            )}
            {d.contaminant && (CONTAMINANT[d.contaminant].depth || d.contaminant === "wet") && (
              <Field label="Depth, mm">
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={d.depthMm ?? ""}
                  onChange={(e) => setD((x) => ({ ...x, depthMm: num(e.target.value) }))}
                  data-testid="urgent-depth"
                />
                {problem("depthMm") && <span className={s.err}>{t(problem("depthMm")!.message)}</span>}
              </Field>
            )}
            {d.contaminant === "compactedSnow" && (
              <label className={s.check}>
                <input
                  type="checkbox"
                  checked={!!d.oatAtOrBelowMinus15}
                  onChange={(e) => setD((x) => ({ ...x, oatAtOrBelowMinus15: e.target.checked }))}
                />
                {t("Outside air temperature at or below −15 °C")}
              </label>
            )}
            {matrix != null && (
              <div className={s.matrix} data-testid="urgent-rwycc">
                <span className={s.code}>{d.downgradeTo != null ? Math.min(matrix, d.downgradeTo) : matrix}</span>
                <span>
                  {t("RWYCC {label}", {
                    label: t(RWYCC_LABEL[d.downgradeTo != null ? Math.min(matrix, d.downgradeTo) : matrix]),
                  })}
                  <div className={ex.muted}>
                    {d.contaminant !== "dry" && (d.coveragePct ?? 0) <= 25
                      ? band == null
                        ? t("under 10% coverage: nothing reported, the third is 6")
                        : t("25% or less coverage: reported as 25%, the third is 6")
                      : t("from the runway condition assessment matrix")}
                  </div>
                </span>
              </div>
            )}
            {matrix != null && matrix > 0 && (
              <div className={s.downgrade}>
                <Field label="Lower the code on observation (optional)">
                  <Select
                    value={d.downgradeTo ?? ""}
                    onChange={(e) =>
                      setD((x) => ({ ...x, downgradeTo: e.target.value === "" ? undefined : Number(e.target.value) }))
                    }
                    aria-label={t("Lower the code on observation (optional)")}
                  >
                    <option value="">{t("No — keep the matrix code")}</option>
                    {[5, 4, 3, 2, 1, 0]
                      .filter((n) => n < matrix)
                      .map((n) => (
                        <option key={n} value={n}>
                          {t(RWYCC_LABEL[n])}
                        </option>
                      ))}
                  </Select>
                </Field>
                {d.downgradeTo != null && (
                  <Field label="What was observed">
                    <Input
                      value={d.downgradeWhy ?? ""}
                      onChange={(e) => setD((x) => ({ ...x, downgradeWhy: e.target.value }))}
                    />
                  </Field>
                )}
                {problem("downgradeTo") && <span className={s.err}>{t(problem("downgradeTo")!.message)}</span>}
              </div>
            )}
          </div>
        )}

        <div className={s.checks}>
          {CHECKS_FOR[area.kind].map((k) => {
            const def = CHECKS.find((c) => c.key === k)!;
            const v = d.checks[k];
            return (
              <div key={k} className={s.checkRow} data-testid={`check-${k}`}>
                <div>
                  <b>{t(def.label)}</b>
                  <div className={ex.muted}>{t(def.what)}</div>
                  {problem(k) && <span className={s.err}>{t(problem(k)!.message)}</span>}
                </div>
                <div className={s.tri} role="group" aria-label={t(def.label)}>
                  {(["ok", "found", "cleared"] as const).map((o) => (
                    <button
                      key={o}
                      type="button"
                      aria-pressed={v === o}
                      className={v === o ? s[`on_${o}`] : ""}
                      onClick={() => set(k, o)}
                    >
                      {t(STATE_LABEL[o])}
                    </button>
                  ))}
                </div>
                {(v === "found" || v === "cleared") && (
                  <Input
                    className={s.noteIn}
                    placeholder={t("What, and exactly where")}
                    value={d.notes?.[k] ?? ""}
                    onChange={(e) => setD((x) => ({ ...x, notes: { ...x.notes, [k]: e.target.value } }))}
                    aria-label={t("What, and exactly where")}
                  />
                )}
              </div>
            );
          })}
        </div>

        <Field label="Remark (optional)">
          <Input value={d.remark ?? ""} onChange={(e) => setD((x) => ({ ...x, remark: e.target.value }))} />
        </Field>

        {tried && problems.length > 0 && (
          <p className={s.err} role="alert">
            {tn(problems.length, "{n} thing to complete before saving.", "{n} things to complete before saving.")}
          </p>
        )}
        {failed && (
          <p className={s.err} role="alert">
            {failed}
          </p>
        )}
        {saved && <p className={ex.saved}>{t("Check saved and added to the log.")}</p>}
        {raised && <p className={ex.saved}>{t("Work order {number} raised.", { number: raised })}</p>}

        <div className={s.actions}>
          <Button onClick={onCancel}>{t("Close")}</Button>
          {found.length > 0 && !raised && (
            <Button onClick={() => void raise()} data-testid="urgent-wo">
              {t("New Work Order for the hazards")}
            </Button>
          )}
          <Button variant="primary" onClick={() => void submit()} disabled={busy} data-testid="urgent-save">
            {busy ? t("Saving…") : prev ? t("Save the re-inspection") : t("Save the check")}
          </Button>
        </div>

        {history.length > 1 && (
          <div className={s.earlier}>
            <div className={ex.colTitle}>{t("Earlier checks of this area in this inspection")}</div>
            <ul className={ex.plain}>
              {history
                .slice(0, -1)
                .reverse()
                .map((c) => {
                  const hist = stateOfCheck(area, c);
                  return (
                    <li key={c.id}>
                      {localStamp(c.at)} · {c.by} ·{" "}
                      <span className={tagFor(hist.verdict)}>{t(VERDICT_LABEL[hist.verdict])}</span>
                      {hist.rwycc != null && ` · RWYCC ${hist.rwycc}`}
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

function stateOfCheck(area: Area, c: AreaCheck): AreaState {
  return statesOf({ id: "x", trigger: "other", openedAt: c.at, openedBy: c.by, checks: [c], log: [] }, [area])[0];
}

/* ── the log, the history, the checklist ─────────────────────────────── */

function Log({ insp }: { insp: UrgentInspection }) {
  const areas = useMemo(() => areasOf(OEMA_MOVEMENT_AREA), []);
  const nameOf = (id: string) => {
    const a = areas.find((x) => x.id === id);
    return a ? areaName(a) : id;
  };
  return (
    <div className={ex.tableWrap}>
      <table className={ex.table} data-testid="urgent-log">
        <tbody>
          {[...insp.log].reverse().map((e, i) => {
            const vars: Record<string, string | number> = { ...e.vars };
            if (typeof vars.area === "string") vars.area = nameOf(vars.area);
            if (typeof vars.verdict === "string")
              vars.verdict = t(VERDICT_LABEL[vars.verdict as Verdict] ?? vars.verdict);
            if (typeof vars.trigger === "string")
              vars.trigger = t(TRIGGERS.find((x) => x.key === vars.trigger)?.label ?? String(vars.trigger));
            if (typeof vars.form === "string")
              vars.form = t(FORMS.find((f) => f.key === vars.form)?.label ?? String(vars.form));
            if (typeof vars.to === "string")
              vars.to = t(RECIPIENTS.find((r) => r.key === vars.to)?.label ?? String(vars.to));
            if (typeof vars.areas === "string")
              vars.areas = vars.areas.split(", ").filter(Boolean).map(nameOf).join(", ");
            return (
              <tr key={i}>
                <td className={s.when}>
                  {localStamp(e.at)}
                  <div className={ex.muted}>{e.by}</div>
                </td>
                <td>{t(e.what, vars)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function History({ list }: { list: UrgentInspection[] }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!list.length) return <EmptyState>{t("No urgent inspection has been closed yet.")}</EmptyState>;
  return (
    <div className={ex.tableWrap}>
      <table className={ex.table} data-testid="urgent-history">
        <tbody>
          {list.map((i) => {
            const mins = Math.round((Date.parse(i.closed!.at) - Date.parse(i.openedAt)) / 60000);
            const trig = TRIGGERS.find((x) => x.key === i.trigger)!;
            return (
              <HistoryRow
                key={i.id}
                open={open === i.id}
                onToggle={() => setOpen(open === i.id ? null : i.id)}
                insp={i}
              >
                <td className={s.when}>{localStamp(i.openedAt)}</td>
                <td>
                  <b>{t(trig.label)}</b>
                  {i.triggerNote && <div className={ex.muted}>{i.triggerNote}</div>}
                </td>
                <td>{t("{n} min", { n: mins })}</td>
                <td>
                  <span className={i.closed!.outcome === "safe" ? ex.tagGreen : ex.tagRed}>
                    {i.closed!.outcome === "safe" ? t("Safe operations confirmed") : t("Closed with restrictions")}
                  </span>
                  {i.closed!.note && <div className={ex.muted}>{i.closed!.note}</div>}
                </td>
                <td className={ex.muted}>{i.closed!.by}</td>
              </HistoryRow>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function HistoryRow({
  insp,
  open,
  onToggle,
  children,
}: {
  insp: UrgentInspection;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const areas = useMemo(() => areasOf(OEMA_MOVEMENT_AREA), []);
  const states = statesOf(insp, areas);
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: "pointer" }} aria-expanded={open}>
        {children}
      </tr>
      {open && (
        <tr>
          <td colSpan={5}>
            <ul className={s.summary}>
              {states.map((x) => (
                <li key={x.area.id}>
                  <span className={tagFor(x.verdict)}>{t(VERDICT_LABEL[x.verdict])}</span> {areaName(x.area)}
                  {x.rwycc != null && ` · RWYCC ${x.rwycc}`}
                  {[...x.hazards, ...x.cleared].map((k) => (
                    <span key={k} className={ex.muted}>
                      {" "}
                      · {t(CHECK_LABEL[k])}
                      {x.latest?.notes?.[k] ? `: ${x.latest.notes[k]}` : ""}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

function Checklist() {
  return (
    <Card
      title="What is checked, and why"
      sub="What to check in each area, by weather type"
    >
      <div className={ex.two}>
        <div>
          {(["runway", "taxiway", "apron"] as const).map((k) => (
            <div key={k}>
              <div className={ex.colTitle}>
                {k === "runway" ? t("Each runway third") : k === "taxiway" ? t("Each taxiway") : t("Each apron")}
              </div>
              <ul className={ex.plain}>
                {k === "runway" && <li>{t("Surface condition, depth and coverage — the runway condition code")}</li>}
                {CHECKS_FOR[k].map((c) => (
                  <li key={c}>{t(CHECK_LABEL[c])}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div>
          <div className={ex.colTitle}>{t("What each event leaves behind")}</div>
          <ul className={ex.plain}>
            {TRIGGERS.filter((x) => x.key !== "other").map((x) => (
              <li key={x.key}>
                <b>{t(x.label)}</b> — {t(x.watch)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}
